import React, { useState } from 'react'
import { Box, styled, Typography } from '@mui/material'
import { isNilOrEmpty, useIsSmallScreen } from 'packages/core'
import { Pagination, ThresholdSearchInput } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'
import { FunctionsList, ItemDetails, PeopleList } from 'components'
import {
    useBusinessRoleGlobalSenstiveFunctions,
    useBusinessRoleOwners,
    useResponsibleParty,
} from 'hooks'
import useSubcomponents from 'useSubcomponents'
import { PersonDetails } from 'components/ApplicationBanner/PersonDetails'

const take = 10
const hasAccessToOwnerControl = 'ITShop-BusinessRoles-Details-Owners-Control'
const hasAccessToResponsiblePartyControl = 'ITShop-BusinessRoles-Details-ResponsibleParty-Control'

const PaddedDiv = styled(Box)({
    padding: '0 31px',
})

export const MoreInformationSection = ({ businessRole }: any) => {
    const { t } = useTranslation()
    const attributeOrientation = useIsSmallScreen() ? 'vertical' : 'horizontal'
    const { hasAccessToControl } = useSubcomponents()
    const isSmallScreen = useIsSmallScreen()

    const [page, setPage] = React.useState(1)
    const [perPageItems, setPerPageItems] = useState(take)
    const [searchKey, setSearchKey] = useState('')

    const handlePageChange = (_: any, value: any) => {
        setPage(value)
    }
    const handlePageSelection = (value: any) => {
        setPage(value)
    }
    const handleItemsPerPageChange = (value: any) => {
        setPage(1)
        setPerPageItems(value)
    }

    const handleSearchChange = (value: any) => {
        setPage(1)
        setSearchKey(value)
    }

    const { data: owners, isInitialLoading: ownersLoading } =
        useBusinessRoleOwners(
            hasAccessToControl(hasAccessToOwnerControl)
                ? businessRole.id
                : null,
        )

    const {
        data: responsibleParty,
        isInitialLoading: responsiblePartyLoading,
    } = useResponsibleParty(
        businessRole.id,
        Boolean(hasAccessToControl(hasAccessToResponsiblePartyControl))
    )

    const { data } = useBusinessRoleGlobalSenstiveFunctions(
        businessRole.id,
        (page - 1) * perPageItems,
        perPageItems,
        searchKey && encodeURIComponent(searchKey),
    )

    const globalSensitiveFunctions = data ? data.data : undefined

    const globalFunctionsLoading = !Boolean(data)

    const numberOfPages = data ? Math.ceil(data.totalCount / perPageItems) : 0

    return (
        <>
            {ownersLoading || responsiblePartyLoading ? (
                <ItemDetails.AttributesContainer>
                    <PaddedDiv>
                        <ItemDetails.AttributesLoader />
                    </PaddedDiv>
                </ItemDetails.AttributesContainer>
            ) : (
                <ItemDetails.AttributesContainer bottomPadding="0">
                    {hasAccessToControl(hasAccessToOwnerControl) && (
                        <Box
                            data-protectedsubcomponent={hasAccessToOwnerControl}
                            padding="0 31px 8.5px 31px"
                        >
                            <ItemDetails.Attribute
                                label={t('BusinessRoles_Owners')}
                                value={<PeopleList list={owners} />}
                                orientation={attributeOrientation}
                            />
                        </Box>
                    )}
                    {hasAccessToControl(hasAccessToResponsiblePartyControl) && (
                        <Box
                            data-protectedsubcomponent={hasAccessToResponsiblePartyControl}
                            padding="0 31px 8.5px 31px"
                        >
                            <ItemDetails.Attribute
                                label={t('Applications_Owner')}
                                value={
                                    responsibleParty ?
                                        <PersonDetails
                                            size="medium"
                                            person={{
                                                imageThumbUrl: responsibleParty.imageThumbUrl,
                                                friendlyName: responsibleParty.friendlyName,
                                                email: responsibleParty.email,
                                                telephone: responsibleParty.telephone,
                                            }}
                                        />
                                        : <>-</>
                                }
                                orientation={attributeOrientation}
                            />
                        </Box>
                    )}
                    {!isNilOrEmpty(searchKey) ||
                        (globalSensitiveFunctions &&
                            globalSensitiveFunctions.length > 0) ? (
                        <>
                            <PaddedDiv
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '16px 31px',
                                }}
                            >
                                <Box width="50%">
                                    <Typography
                                        style={{
                                            fontSize: '12px',
                                            lineHeight: 1.33,
                                            color: '#9b9b9b',
                                            wordBreak: 'break-word',
                                            textTransform: 'uppercase',
                                            minWidth: '80px',
                                        }}
                                    >
                                        {t('Common_GlobalFunctionalAccess')}
                                    </Typography>
                                </Box>

                                <Box
                                    width={!isSmallScreen ? '308px' : '50%'}
                                    minWidth="130px"
                                >
                                    <ThresholdSearchInput
                                        placeholder={'Common_Search'}
                                        handleSearch={handleSearchChange}
                                    />
                                </Box>
                            </PaddedDiv>
                            <Box overflow="auto">
                                <FunctionsList
                                    data={globalSensitiveFunctions}
                                    loading={globalFunctionsLoading}
                                />

                                <Box
                                    padding={
                                        !isSmallScreen ? '20px 32px' : '16px'
                                    }
                                    display="flex"
                                    alignItems="center"
                                    justifyContent={
                                        !isSmallScreen ? 'flex-start' : 'center'
                                    }
                                >
                                    <Pagination
                                        count={numberOfPages}
                                        size={
                                            !isSmallScreen ? 'large' : 'small'
                                        }
                                        page={page}
                                        onChange={handlePageChange}
                                        totalCount={data?.totalCount}
                                        onPageSelection={handlePageSelection}
                                        itemsPerPage={perPageItems}
                                        onItemsPerPageChange={
                                            handleItemsPerPageChange
                                        }
                                        showPageSelection={true}
                                        showItemsPerPageSelection
                                        showCountStat
                                    />
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Box marginY="20px" />
                    )}
                </ItemDetails.AttributesContainer>
            )}
        </>
    )
}
